import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, FileText, ArrowLeft, Copy } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { CITIZEN } from "@/constants/testIds";

const META = {
  solar_rooftop:    { title: "Solar Rooftop Application", desc: "Domestic & commercial rooftop solar with MNRE + PEDA subsidy.", cap: true },
  street_light:     { title: "Street Lighting", desc: "LED and smart street light request for panchayats and ULBs.", cap: false },
  biogas:           { title: "Biogas Plant Application", desc: "Rural biogas installation — family or community sized.", cap: true },
  cbg:              { title: "Compressed Biogas (CBG)", desc: "Industrial CBG developer empanelment and plant proposal.", cap: true },
  waste_to_energy:  { title: "Waste-to-Energy Proposal", desc: "Municipal or industrial waste-to-energy project intake.", cap: true },
  ev:               { title: "EV Scheme Application", desc: "EV incentive or charging station setup application.", cap: false },
  net_metering:     { title: "Net Metering Registration", desc: "Bi-directional metering & discom coordination.", cap: true },
  subsidy:          { title: "Subsidy Application", desc: "Generic PEDA subsidy application.", cap: false },
};

const DISTRICTS = ["Ludhiana","Amritsar","Jalandhar","Patiala","Bathinda","Mohali","Sangrur","Hoshiarpur","Ferozepur","Moga","Faridkot","Kapurthala","Barnala","Fazilka","Gurdaspur","Mansa","Muktsar","Nawanshahr","Rupnagar","Tarn Taran","Pathankot","Malerkotla"];

export default function ServiceDetailPage() {
  const { scheme } = useParams();
  const meta = META[scheme] || { title: "Service", desc: "", cap: false };
  const nav = useNavigate();
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    applicant_name: "", address: "", district: "Ludhiana", phone: "", email: "", capacity_kw: "", notes: ""
  });

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = { ...form, scheme, capacity_kw: form.capacity_kw ? Number(form.capacity_kw) : undefined };
      const { data } = await api.post("/applications", payload);
      setResult(data);
    } catch (e) {
      if (e.response?.status === 401) nav("/login?next=/services/" + scheme);
      else setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="container-x py-16 max-w-2xl">
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-8 text-center">
          <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500"/>
          <h1 className="mt-4 text-3xl font-heading font-bold">Application submitted!</h1>
          <p className="mt-2 text-muted-foreground">Save your tracking ID. You'll receive SMS/email updates.</p>
          <div className="mt-6 mx-auto max-w-sm rounded-xl bg-card border border-border p-4 flex items-center justify-between gap-3">
            <div>
              <div className="label-tiny text-muted-foreground">Tracking ID</div>
              <div className="font-mono font-semibold text-lg">{result.tracking_id}</div>
            </div>
            <button className="btn-outline" onClick={()=>navigator.clipboard.writeText(result.tracking_id)}><Copy className="h-4 w-4"/></button>
          </div>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/track" className="btn-primary">Track Status</Link>
            <Link to="/services" className="btn-outline">Back to Services</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-12 grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-1">
        <Link to="/services" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4"/>All Services</Link>
        <div className="label-tiny mt-4 text-[hsl(var(--primary))]">Scheme</div>
        <h1 className="mt-1 text-3xl font-heading font-bold">{meta.title}</h1>
        <p className="mt-2 text-muted-foreground">{meta.desc}</p>
        <div className="mt-6 rounded-xl border border-border bg-card p-5 text-sm">
          <div className="font-heading font-semibold flex items-center gap-2"><FileText className="h-4 w-4"/>Documents required</div>
          <ul className="mt-3 space-y-2 text-muted-foreground list-disc list-inside">
            <li>Identity proof (Aadhaar/PAN)</li>
            <li>Address proof / electricity bill</li>
            <li>Site photograph (for solar/biogas)</li>
            <li>Bank passbook (for subsidy transfer)</li>
          </ul>
        </div>
      </div>
      <form onSubmit={submit} className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-heading font-bold text-xl">Application form</h2>
        {error && <div className="mt-3 rounded-md bg-red-500/10 text-red-500 px-3 py-2 text-sm">{error}</div>}
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <Field label="Applicant name" required>
            <input required value={form.applicant_name} onChange={change("applicant_name")} className="input"/>
          </Field>
          <Field label="Phone" required>
            <input required value={form.phone} onChange={change("phone")} className="input"/>
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={change("email")} className="input"/>
          </Field>
          <Field label="District" required>
            <select required value={form.district} onChange={change("district")} className="input">
              {DISTRICTS.map(d=><option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Address" required cols="2">
            <textarea required rows={2} value={form.address} onChange={change("address")} className="input"/>
          </Field>
          {meta.cap && (
            <Field label="Capacity (kW)">
              <input type="number" step="0.1" value={form.capacity_kw} onChange={change("capacity_kw")} className="input"/>
            </Field>
          )}
          <Field label="Notes / Remarks" cols="2">
            <textarea rows={3} value={form.notes} onChange={change("notes")} className="input" placeholder="Any additional detail…"/>
          </Field>
        </div>
        <button type="submit" disabled={loading} data-testid={CITIZEN.submitApp} className="btn-primary mt-6">
          {loading ? "Submitting…" : "Submit application"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, required, cols, children }) {
  return (
    <label className={`flex flex-col gap-1.5 ${cols === "2" ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-semibold text-muted-foreground">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</span>
      {children}
    </label>
  );
}
